import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { KTIcon } from '../../../../../../../_metronic/helpers'
import { AppSelect } from '../../../../../../../components/Select'
import { convertUTCtoMSK, formatSum } from '../../../../../../../utils'
import { createPayoutDispute, getCulpritCandidates, getDisputeReasonLookup } from '../../core/api'
import { PAYOUT_DISPUTE_CREATE_OUTCOMES } from '../../core/consts'
import { buildDisputeErrorText, disputeErrorFromException } from '../../core/errors'
import { DisputeCreateResolution } from '../../core/requests.types'
import { CulpritCandidate, DisputeRecord, DisputeReasonLookupItem } from '../../core/response.types'
import { usePayoutCulpritDrafts } from '../../core/usePayoutCulpritDrafts'
import { PayoutCulpritRemedyBlock } from '../PayoutCulpritRemedyBlock/PayoutCulpritRemedyBlock'

interface Props {
  show: boolean
  onClose: () => void
  payoutToken: string
  onCreated?: (record: DisputeRecord) => void
}

interface FormShape {
  reasonId: number | ''
  comment: string
  culpritTokens: string[]
  resolution: DisputeCreateResolution
}

const schema = Yup.object().shape({
  reasonId: Yup.number().integer().min(1).required('Выберите причину'),
  comment: Yup.string().max(1000, 'Не более 1000 символов'),
  culpritTokens: Yup.array().of(Yup.string()).min(1, 'Выберите хотя бы один депозит'),
})

/**
 * Диспут по выплате: виновники — оплаченные депозиты этой выплаты, их отдаёт бэкенд. Выбор
 * виновников фиксируется при создании; ни выплата, ни депозиты от создания не меняются.
 */
export const PayoutDisputeModal: React.FC<Props> = ({ show, onClose, payoutToken, onCreated }) => {
  const [reasons, setReasons] = useState<DisputeReasonLookupItem[]>([])
  const [candidates, setCandidates] = useState<CulpritCandidate[] | null>(null)
  const [generalError, setGeneralError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const formik = useFormik<FormShape>({
    initialValues: { reasonId: '', comment: '', culpritTokens: [], resolution: 'NONE' },
    validationSchema: schema,
    onSubmit: async (values) => {
      setSubmitting(true)
      setGeneralError(null)
      try {
        const response = await createPayoutDispute({
          payoutTicketToken: payoutToken,
          culpritTokens: values.culpritTokens,
          reasonId: Number(values.reasonId),
          ...(values.comment ? { comment: values.comment } : {}),
          resolution: values.resolution,
          ...(values.resolution === 'SUCCESS' ? { culprits: culprits.facts } : {}),
        })

        if (response.data?.success && response.data.data) {
          onCreated?.(response.data.data)
          onClose()
          return
        }

        setGeneralError(buildDisputeErrorText(response.data, 'Не удалось создать диспут'))
      } catch (e: unknown) {
        setGeneralError(disputeErrorFromException(e, 'Не удалось создать диспут'))
      } finally {
        setSubmitting(false)
      }
    },
  })

  // A dispute created already resolved in the client's favour carries the same culprit facts as a
  // later success; the candidates are the ones this window has loaded.
  const culprits = usePayoutCulpritDrafts(
    show && formik.values.resolution === 'SUCCESS',
    payoutToken,
    formik.values.culpritTokens,
    candidates
  )

  useEffect(() => {
    if (!show) {
      formik.resetForm()
      setGeneralError(null)
      setCandidates(null)
      return
    }

    getDisputeReasonLookup().then((res) => {
      if (res.data?.success && res.data.data) {
        setReasons(res.data.data)
      }
    })

    getCulpritCandidates(payoutToken)
      .then((res) => {
        if (res.data?.success && res.data.data) {
          setCandidates(res.data.data)
          return
        }
        setCandidates([])
        setGeneralError(buildDisputeErrorText(res.data, 'Не удалось загрузить депозиты выплаты'))
      })
      .catch((e: unknown) => {
        setCandidates([])
        setGeneralError(disputeErrorFromException(e, 'Не удалось загрузить депозиты выплаты'))
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, payoutToken])

  const toggleCulprit = (token: string) => {
    const selected = formik.values.culpritTokens
    formik.setFieldValue(
      'culpritTokens',
      selected.includes(token) ? selected.filter((t) => t !== token) : [...selected, token]
    )
  }

  const currentReason = reasons.find((r) => r.value === formik.values.reasonId) ?? null
  const outcome =
    PAYOUT_DISPUTE_CREATE_OUTCOMES.find((o) => o.value === formik.values.resolution) ??
    PAYOUT_DISPUTE_CREATE_OUTCOMES[0]
  const canSubmit =
    !submitting &&
    formik.values.reasonId !== '' &&
    formik.values.culpritTokens.length > 0 &&
    (formik.values.resolution !== 'SUCCESS' || culprits.complete)

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Создать диспут по выплате</Modal.Title>
      </Modal.Header>
      <form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <div className='mb-4'>
            <div className='text-muted small'>Выплата</div>
            <div className='text-monospace'>{payoutToken}</div>
          </div>

          <div className='mb-4'>
            <div className='form-label required'>Виновники</div>
            {candidates === null ? (
              <div className='text-muted'>Загрузка…</div>
            ) : candidates.length === 0 ? (
              <div className='text-muted'>Оплаченных депозитов у выплаты нет</div>
            ) : (
              candidates.map((c) => (
                <div key={c.token} className='form-check form-check-custom mb-2'>
                  <input
                    type='checkbox'
                    id={`culprit-${c.token}`}
                    className='form-check-input'
                    checked={formik.values.culpritTokens.includes(c.token)}
                    onChange={() => toggleCulprit(c.token)}
                    disabled={submitting}
                  />
                  <label className='form-check-label' htmlFor={`culprit-${c.token}`}>
                    <span className='text-monospace'>{c.merchantTransactionId ?? c.token}</span>
                    {c.amount !== null && <span className='text-muted'>{` · ${formatSum(c.amount)} ${c.currency}`}</span>}
                    {c.completionDate && <span className='text-muted'>{` · ${convertUTCtoMSK(c.completionDate)}`}</span>}
                  </label>
                </div>
              ))
            )}
            {formik.touched.culpritTokens && formik.errors.culpritTokens && (
              <div className='text-danger small'>{formik.errors.culpritTokens as string}</div>
            )}
          </div>

          <div className='mb-4'>
            <div className='form-label required'>Исход</div>
            {PAYOUT_DISPUTE_CREATE_OUTCOMES.map((o) => (
              <div key={o.value} className='form-check form-check-custom mb-2'>
                <input
                  type='radio'
                  id={`payout-dispute-outcome-${o.value}`}
                  name='resolution'
                  className='form-check-input'
                  checked={formik.values.resolution === o.value}
                  onChange={() => formik.setFieldValue('resolution', o.value)}
                  disabled={submitting}
                />
                <label className='form-check-label' htmlFor={`payout-dispute-outcome-${o.value}`}>
                  {o.label}
                </label>
              </div>
            ))}
          </div>

          {formik.values.resolution === 'SUCCESS' && (
            <div className='mb-4'>
              {culprits.loading && <div className='text-muted'>Загрузка…</div>}
              {culprits.candidates.map((c) => (
                <PayoutCulpritRemedyBlock
                  key={c.token}
                  candidate={c}
                  draft={culprits.draftOf(c.token)}
                  disabled={submitting}
                  onChange={(draft) => culprits.setDraft(c.token, draft)}
                />
              ))}
            </div>
          )}

          <div className='notice d-flex bg-light-warning rounded border-warning border border-dashed p-4 mb-4'>
            <KTIcon iconName='information-5' className='fs-2x text-warning me-3' />
            <div className='fs-7 text-gray-700'>{outcome.notice}</div>
          </div>

          <div className='mb-3'>
            <label className='form-label required'>Причина</label>
            <AppSelect<DisputeReasonLookupItem>
              options={reasons}
              value={currentReason}
              onChange={(opt) => formik.setFieldValue('reasonId', opt?.value ?? '')}
              placeholder='Выберите причину'
              isClearable={false}
              aria-label='payout-dispute-reason-select'
              isDisabled={submitting}
            />
            {formik.touched.reasonId && formik.errors.reasonId && (
              <div className='text-danger small'>{formik.errors.reasonId as string}</div>
            )}
          </div>

          <div className='mb-3'>
            <label className='form-label' htmlFor='payout-dispute-comment'>
              Комментарий
            </label>
            <textarea
              id='payout-dispute-comment'
              className='form-control'
              rows={3}
              maxLength={1000}
              {...formik.getFieldProps('comment')}
              disabled={submitting}
            />
          </div>

          {generalError && <div className='alert alert-danger mt-4 mb-0'>{generalError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={onClose} disabled={submitting}>
            Отмена
          </button>
          <button type='submit' className='btn btn-primary' disabled={!canSubmit}>
            {submitting ? 'Создаём…' : outcome.submit}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default PayoutDisputeModal
